import { BENCH_POSITIONS, type LeagueData, type Player } from "../../schemas.js";
import { actualLineup, gameFor, optimalLineup, type Game, type Lineup } from "../data.js";
import { href } from "../router.js";
import { backLink, esc, fmt1, gameRow, pressSegment, segmented } from "../ui.js";
import { notFoundView } from "./not-found.js";
import type { View } from "./view.js";

type LineupView = "actual" | "optimal";

// Which lineups to compare; kept as you move between matchups.
let lineupView: LineupView = "optimal";

export function matchupView(league: LeagueData, week: number, teamId: string): View {
  const game = gameFor(league, week, teamId);
  if (!game) return notFoundView(`No week ${week} game found for that team.`);

  const toggle = segmented(
    "lineup",
    "Lineups to compare",
    [["actual", "As started"], ["optimal", "Best possible"]],
    lineupView,
  );

  const html = `
    ${backLink(href.week(week), `Week ${week} matchups`)}
    <section class="card">
      <div class="card-head title-row">
        <h2>Week ${week}</h2>
      </div>
      ${gameRow(game, false)}
    </section>
    <section class="card lineups-card">
      <div class="card-head title-row">
        <h2>Lineups</h2>
        ${toggle}
      </div>
      <div class="scroll" id="lineup-table" tabindex="0" role="region" aria-label="Lineup comparison">${compareTable(league, game, lineupView)}</div>
    </section>`;

  return {
    title: `${game.a.team.name} vs ${game.b.team.name} · Week ${week}`,
    html,
    mount(root) {
      root.querySelectorAll<HTMLButtonElement>("button[data-lineup]").forEach((b) =>
        b.addEventListener("click", () => {
          lineupView = b.dataset["lineup"] as LineupView;
          pressSegment("lineup", lineupView);
          root.querySelector("#lineup-table")!.innerHTML = compareTable(league, game, lineupView);
        }),
      );
    },
  };
}

function compareTable(league: LeagueData, game: Game, view: LineupView): string {
  const build = view === "actual" ? actualLineup : optimalLineup;
  const a = build(league, game.a.tw);
  const b = build(league, game.b.tw);

  // Players in each team's best lineup, to flag the ones that were misplaced.
  const bestA = new Set(optimalLineup(league, game.a.tw).starters.map((r) => r.player));
  const bestB = new Set(optimalLineup(league, game.b.tw).starters.map((r) => r.player));

  const mark = (p: Player, best: Set<Player | null>, inLineup: boolean): string => {
    const wasBenched = BENCH_POSITIONS.has(p.selectedPosition);
    if (view === "actual") {
      if (!inLineup && best.has(p)) return "promote";
      if (inLineup && !best.has(p)) return "demote";
    } else {
      if (inLineup && wasBenched) return "promote";
      if (!inLineup && !wasBenched) return "demote";
    }
    return "";
  };

  const cells = (p: Player | null, best: Set<Player | null>, inLineup: boolean, side: "a" | "b") => {
    if (!p) return side === "a" ? `<td class="pl a empty">—</td><td class="pts a"></td>` : `<td class="pts b"></td><td class="pl b empty">—</td>`;
    const cls = mark(p, best, inLineup);
    const name = `<td class="pl ${side} ${cls}"><span class="pname">${esc(p.name)}</span><span class="pmeta">${esc(
      [p.nflTeam, p.positions.join("/")].filter(Boolean).join(" · "),
    )}</span></td>`;
    const pts = `<td class="pts ${side} ${cls}">${fmt1(p.points)}</td>`;
    return side === "a" ? name + pts : pts + name;
  };

  const starterRows = zip(a.starters, b.starters)
    .map(
      ([ra, rb]) => `
      <tr>
        ${cells(ra?.player ?? null, bestA, true, "a")}
        <td class="slot">${esc(ra?.slot ?? rb?.slot ?? "")}</td>
        ${cells(rb?.player ?? null, bestB, true, "b")}
      </tr>`,
    )
    .join("");

  const benchLabel = (p: Player | undefined) => (view === "actual" && p ? p.selectedPosition : "BN");
  const benchRows = zip(a.bench, b.bench)
    .map(
      ([pa, pb]) => `
      <tr>
        ${cells(pa ?? null, bestA, false, "a")}
        <td class="slot">${esc(benchLabel(pa ?? pb))}</td>
        ${cells(pb ?? null, bestB, false, "b")}
      </tr>`,
    )
    .join("");

  return `
    <table class="lineup-cmp">
      <thead><tr>
        <th class="pl a">${esc(game.a.team.name)}</th><th class="pts a">Pts</th>
        <th class="slot">Pos</th>
        <th class="pts b">Pts</th><th class="pl b">${esc(game.b.team.name)}</th>
      </tr></thead>
      <tbody>${starterRows}</tbody>
      <tbody class="total">${totalRow(a, b, game.final)}</tbody>
      <tbody class="bench">
        <tr class="section"><td colspan="5">Bench</td></tr>
        ${benchRows}
      </tbody>
    </table>`;
}

const totalRow = (a: Lineup, b: Lineup, final: boolean): string => `
  <tr>
    <td class="pl a"></td><td class="pts a${final && a.total > b.total ? " win" : ""}">${fmt1(a.total)}</td>
    <td class="slot">Total</td>
    <td class="pts b${final && b.total > a.total ? " win" : ""}">${fmt1(b.total)}</td><td class="pl b"></td>
  </tr>`;

function zip<T, U>(xs: T[], ys: U[]): Array<[T | undefined, U | undefined]> {
  return Array.from({ length: Math.max(xs.length, ys.length) }, (_, i) => [xs[i], ys[i]]);
}
